import axios from 'axios';
import { debugLog } from '../utils/debug';
import { 
  Mission, 
  MissionListResponse, 
  CreateMissionRequest, 
  UpdateMissionRequest, 
  MissionFilters,
  ApiResponse,
  MissionComment,
  MissionPatient,
  MissionAircraft,
  MissionRadioFrequency
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('teaam_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('teaam_token');
      localStorage.removeItem('teaam_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const missionService = {
  // Get all missions with optional filters
  async getMissions(filters?: MissionFilters & { viewMode?: 'active' | 'all' }): Promise<MissionListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        // View mode (active = exclude closed missions)
        if (filters.viewMode) params.append('viewMode', filters.viewMode);

        // Refinement filters
        if (filters.status?.length) params.append('status', filters.status.join(','));
        if (filters.priority?.length) params.append('priority', filters.priority.join(','));
        if (filters.missionType?.length) params.append('missionType', filters.missionType.join(','));
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
        if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
        if (filters.search) params.append('search', filters.search);
        if (filters.includeTestTraining !== undefined) {
          params.append('includeTestTraining', filters.includeTestTraining.toString());
        }
      }

      const response = await apiClient.get<ApiResponse<MissionListResponse>>(
        `/missions?${params.toString()}`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch missions');
      }

      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch missions');
    }
  },

  // Get single mission by ID
  async getMission(id: string): Promise<Mission> {
    try {
      const response = await apiClient.get<ApiResponse<Mission>>(`/missions/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch mission');
      }
      
      debugLog('missionService.getMission: Response data', {
        patientCount: response.data.data.patientCount,
        patientsLength: response.data.data.patients?.length,
        hasPatients: !!response.data.data.patients
      });
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch mission details');
    }
  },

  // Create new mission
  async createMission(data: CreateMissionRequest): Promise<Mission> {
    try {
      const response = await apiClient.post<ApiResponse<Mission>>('/missions', data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create mission');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create mission');
    }
  },

  // Update mission
  async updateMission(id: string, data: UpdateMissionRequest): Promise<Mission> {
    try {
      const response = await apiClient.put<ApiResponse<Mission>>(`/missions/${id}`, data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update mission');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update mission');
    }
  },

  // Update mission status
  async updateMissionStatus(id: string, status: string): Promise<Mission> {
    return this.updateMission(id, { status });
  },

  // Add comment to mission
  async addComment(missionId: string, commentText: string): Promise<MissionComment> {
    try {
      const response = await apiClient.post<ApiResponse<MissionComment>>(
        `/missions/${missionId}/comments`,
        { commentText }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to add comment');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to add comment');
    }
  },

  // Add patient to mission
  async addPatient(missionId: string, patientData: Partial<MissionPatient>): Promise<MissionPatient> {
    try {
      const response = await apiClient.post<ApiResponse<MissionPatient>>(
        `/missions/${missionId}/patients`,
        patientData
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to add patient');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to add patient');
    }
  },

  // Update patient
  async updatePatient(missionId: string, patientId: string, patientData: Partial<MissionPatient>): Promise<MissionPatient> {
    try {
      const response = await apiClient.put<ApiResponse<MissionPatient>>(
        `/missions/${missionId}/patients/${patientId}`,
        patientData
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update patient');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update patient');
    }
  },

  // Remove patient from mission
  async removePatient(missionId: string, patientId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/missions/${missionId}/patients/${patientId}`
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to remove patient');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to remove patient');
    }
  },

  // Add aircraft/contractor to mission
  async addAircraft(missionId: string, aircraftData: { contractorId: string; notes?: string }): Promise<MissionAircraft> {
    try {
      const response = await apiClient.post<ApiResponse<MissionAircraft>>(
        `/missions/${missionId}/aircraft`,
        aircraftData
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to add aircraft');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to add aircraft');
    }
  },

  // Remove aircraft from mission
  async removeAircraft(missionId: string, aircraftId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/missions/${missionId}/aircraft/${aircraftId}`
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to remove aircraft');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to remove aircraft');
    }
  },

  // Update EMS timestamp
  async updateTimestamp(id: string, timestampField: string, value: Date | null): Promise<Mission> {
    return this.updateMission(id, { [timestampField]: value });
  },

  // Add radio frequency to mission
  async addRadioFrequency(missionId: string, data: { name: string; frequency: string; type: string }): Promise<MissionRadioFrequency> {
    try {
      const response = await apiClient.post<ApiResponse<MissionRadioFrequency>>(
        `/missions/${missionId}/frequencies`,
        data
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to add frequency');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to add frequency');
    }
  },

  // Remove radio frequency from mission
  async removeRadioFrequency(missionId: string, frequencyId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/missions/${missionId}/frequencies/${frequencyId}`
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to remove frequency');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to remove frequency');
    }
  },

  // Get mission statistics
  async getStatistics(): Promise<{
    activeMissions: number;
    todayMissions: number;
    criticalMissions: number;
    avgResponseTime: number;
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>('/missions/statistics');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch statistics');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch statistics');
    }
  },

  // Get audit logs for a mission
  async getAuditLogs(missionId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(
        `/audit/mission/${missionId}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch audit logs');
    }
  },

  // Send Connect Rocket alert for mission
  async sendAlert(missionId: string, options?: { lists?: string[]; send_via?: string[] }): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse<void>>(
        `/missions/${missionId}/send-alert`,
        options || {}
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send alert');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to send alert');
    }
  },
};

export default missionService;